import {
  connectionResponseSchema,
  lineResponseSchema,
  lineStatusResponseSchema,
  registerLineResponseSchema,
  simulateInboundResponseSchema,
  simulateSeedResponseSchema,
  subscribeResponseSchema,
  syncStatusResponseSchema,
  userResponseSchema,
  type ConnectionState,
  type Line,
  type LineStatusResponse,
  type Role,
  type SimulateInboundBody,
  type SimulateInboundResponse,
  type SimulateSeedResponse,
  type SyncStatus,
  type User,
} from '@chatapp-inbox/shared';
import { isApiError, request } from './http';

// Rotas de administração: SÓ ADMINISTRADOR (atendente recebe 403 em todas). A lista de usuários
// (`GET /admin/users`) já vive em `users.ts`, porque a atribuição de conversas também a usa.

export interface NewUserInput {
  name: string;
  email: string;
  password: string;
  role: Role;
}

/** Cria um usuário ativo. A senha só trafega neste pedido: a resposta nunca a devolve. */
export async function createUser(input: NewUserInput): Promise<User> {
  const { user } = await request('/admin/users', {
    method: 'POST',
    body: input,
    schema: userResponseSchema,
  });
  return user;
}

/**
 * Alterações aceitas pelo servidor. Só os campos presentes são enviados (o servidor recusa corpo
 * vazio e campos desconhecidos), e a senha, quando presente, encerra as sessões da pessoa.
 */
export interface UserPatch {
  name?: string;
  role?: Role;
  active?: boolean;
  password?: string;
}

export async function updateUser(id: string, patch: UserPatch): Promise<User> {
  const { user } = await request(`/admin/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: patch,
    schema: userResponseSchema,
  });
  return user;
}

const WHATSAPP = '/admin/whatsapp';

/**
 * A resposta da conexão nunca deve trazer valor de campo secreto (o contrato diz que o segredo não
 * volta). Se um servidor errado o mandasse em `values`, ele seguiria para o cache e para o
 * formulário; aqui é descartado antes de sair da camada de API.
 */
function withoutSecretValues(connection: ConnectionState): ConnectionState {
  const secretKeys = new Set(connection.fields.filter((f) => f.secret).map((f) => f.key));
  if (secretKeys.size === 0) return connection;
  const values = Object.fromEntries(
    Object.entries(connection.values).filter(([key]) => !secretKeys.has(key)),
  );
  return { ...connection, values };
}

/**
 * A conexão do WhatsApp: o que o provedor pede (`fields`), os valores atuais não secretos, quais
 * segredos já estão guardados, os números e o que esta instalação oferece (importação, demonstração).
 */
export async function getConnection(signal?: AbortSignal): Promise<ConnectionState> {
  const { connection } = await request(WHATSAPP, {
    schema: connectionResponseSchema,
    ...(signal ? { signal } : {}),
  });
  return withoutSecretValues(connection);
}

/**
 * Salva as credenciais e redescobre os números. `values` leva só o que a pessoa preencheu: campo
 * secreto em branco NÃO é enviado (o servidor mantém o guardado), então nunca vai `""` no lugar de
 * um segredo. Quem chama já decidiu o que entra; esta função não aplica `trim` a nada.
 */
export async function saveConnection(values: Record<string, string>): Promise<ConnectionState> {
  const { connection } = await request(`${WHATSAPP}/connection`, {
    method: 'PUT',
    body: { values },
    schema: connectionResponseSchema,
  });
  return withoutSecretValues(connection);
}

/** Procura os números de novo (novos entram ligados; os que sumiram ficam desligados). */
export async function refreshLines(): Promise<ConnectionState> {
  const { connection } = await request(`${WHATSAPP}/lines/refresh`, {
    method: 'POST',
    schema: connectionResponseSchema,
  });
  return withoutSecretValues(connection);
}

/**
 * Inscreve a conta no app do serviço de mensagens (na Meta, sem isto as mensagens não chegam ao
 * webhook). Pode repetir sem efeito colateral. Só vale com a conexão pronta (`canSubscribe`).
 */
export async function subscribeAccount(): Promise<void> {
  await request(`${WHATSAPP}/subscribe`, { method: 'POST', schema: subscribeResponseSchema });
}

/** Liga ou desliga um número (desligado não recebe nem envia; as conversas dele continuam legíveis). */
export async function setLineEnabled(id: string, enabled: boolean): Promise<Line> {
  const { line } = await request(`${WHATSAPP}/lines/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: { enabled },
    schema: lineResponseSchema,
  });
  return line;
}

/** A situação do número no serviço de mensagens (na Meta: conectado ou pendente de registro, nome, qualidade). */
export async function fetchLineStatus(id: string): Promise<LineStatusResponse> {
  return request(`${WHATSAPP}/lines/${encodeURIComponent(id)}/status`, {
    schema: lineStatusResponseSchema,
  });
}

/**
 * Conclui o registro de um número "Pendente" com o PIN de 6 dígitos (a verificação em duas etapas do número). O PIN só
 * trafega neste pedido: o servidor o repassa ao serviço e não o guarda.
 */
export async function registerLine(id: string, pin: string): Promise<void> {
  await request(`${WHATSAPP}/lines/${encodeURIComponent(id)}/register`, {
    method: 'POST',
    body: { pin },
    schema: registerLineResponseSchema,
  });
}

/**
 * Andamento da importação de histórico. `null` = este servidor não tem importação (as rotas
 * respondem 404): a tela esconde a seção em vez de mostrar erro.
 */
export async function getSync(signal?: AbortSignal): Promise<SyncStatus | null> {
  try {
    const { status } = await request(`${WHATSAPP}/sync`, {
      schema: syncStatusResponseSchema,
      ...(signal ? { signal } : {}),
    });
    return status;
  } catch (err) {
    if (isApiError(err) && err.status === 404) return null;
    throw err;
  }
}

/** Inicia a importação (202). Já em andamento = `ApiError` 409 `already_running`. */
export async function startSync(): Promise<SyncStatus> {
  const { status } = await request(`${WHATSAPP}/sync`, {
    method: 'POST',
    schema: syncStatusResponseSchema,
  });
  return status;
}

/** Demonstração: grava uma mensagem de cliente pelo mesmo caminho da entrada real. 404 fora da demonstração. */
export function simulateInbound(body: SimulateInboundBody): Promise<SimulateInboundResponse> {
  return request(`${WHATSAPP}/simulate/inbound`, {
    method: 'POST',
    body,
    schema: simulateInboundResponseSchema,
  });
}

/** Demonstração: cria conversas de exemplo nos números ligados (0 = já existiam). */
export function seedSimulation(): Promise<SimulateSeedResponse> {
  return request(`${WHATSAPP}/simulate/seed`, {
    method: 'POST',
    schema: simulateSeedResponseSchema,
  });
}
